import ClassFilterSelect from '@/components/ClassFilterSelect';
import { DataTable } from '@/components/data-table';
import ContentLayout from '@/components/layout/ContentLayout';
import StudentMessageDialog from '@/components/student-message-dialog';
import { HomeworkPreviewDialog, PreviewMode } from '@/components/homework-preview-dialog';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import Survey6 from '@/components/survey6';

import { useAuth } from '@/contexts/AuthContext';
import { ROUTE } from '@/routes/constants';
import { getAllClassInfo, getAssignmentListByTeacher, getStudentMessage, deleteAssignment, getQuiz, querySurveyV2 } from '@/services/generatedAPIs';
import { useSubjectList } from '@/hooks/use-subject-list';
import { getSubjectDisplayName } from '@/hooks/use-subject-name';
import { AssignmentModel, AssignmentStats, Class, StudentMessage, QuestionInfo } from '@/services/generatedAPIs.schemas';
import { ColumnDef } from '@tanstack/react-table';
import { differenceInDays, format } from 'date-fns';
import { ArrowUpDown, User } from 'lucide-react';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { isEN } from '@/utils/i18n';
import { NavLink } from 'react-router';

type TableData = {
  title?: string;
  description?: string;
  dueDate?: string;
  createdAt?: string;
  subject: string;
  topicList: string[];
  classInfo: Class;
  totalCount: number;
  submittedCount: number;
  correctCount: number;
  errorKnowledgePoints: string[];
  assignmentId: number;
}

export type ClassTableData = {
  title?: string;
  id: number;
  name: string;
  studentCount: number;
}

const HomeworkPage = () => {
  const { t, i18n } = useTranslation();
  const { getCurrentUser } = useAuth();
  const { subjectInfos } = useSubjectList();
  const [showSurvey6, setShowSurvey6] = React.useState(false);

  React.useEffect(() => {
    const user = getCurrentUser();
    if (!user?.registeredAt) return;
    const daysSinceRegistration = differenceInDays(new Date().toISOString(), new Date(user.registeredAt + 'Z'));
    if (daysSinceRegistration < 30) return;
    querySurveyV2({ userId: user.id, surveyId: 6 }).then((rsp) => {
      if (rsp.code === 200 && !rsp.data?.hasRecord) {
        setShowSurvey6(true);
      }
    }).catch(() => {});
  }, [getCurrentUser]);

  const [tableData, setTableData] = React.useState<TableData[]>();
  const [allTableData, setAllTableData] = React.useState<TableData[]>(); // 存储所有数据
  const [classData, setClassData] = React.useState<ClassTableData[]>();
  const [selectedClassId, setSelectedClassId] = React.useState<string>("all"); // 班别筛选状态
  const [studentMessages, setStudentMessages] = useState<StudentMessage[]>([]);
  const [homeworkReviews, setHomeworkReviews] = useState<StudentMessage[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewQuestions, setPreviewQuestions] = useState<QuestionInfo[]>([]);
  const [previewData, setPreviewData] = useState<{ title?: string; description?: string; subject?: string; unit?: string; className?: string; dueDate?: string }>();



  const handlePreview = useRef(async (info: TableData) => {
    try {
      const rsp = await getAssignmentListByTeacher({ teacherId: getCurrentUser()!.id });
      if (rsp.code === 200 && rsp.data) {
        const assignment = rsp.data.assignmentList.find((a: AssignmentModel) => a.assignment.id === info.assignmentId);
        const quizId = assignment?.assignment.quizId;
        if (quizId) {
          const quizRsp = await getQuiz({ quizId });
          if (quizRsp.code === 200 && quizRsp.data?.questionInfos) {
            setPreviewQuestions(quizRsp.data.questionInfos);
          } else {
            setPreviewQuestions([]);
          }
        } else {
          setPreviewQuestions([]);
        }
      }
    } catch {
      setPreviewQuestions([]);
    }
    setPreviewData({
      title: info.title,
      description: info.description,
      subject: info.subject,
      unit: info.topicList.join(', '),
      className: info.classInfo.name,
      dueDate: info.dueDate,
    });
    setPreviewOpen(true);
  });

  const [columns] = React.useState<ColumnDef<TableData>[]>([
    {
      id: "select",
      header: () => (<></>),
      cell: () => (<></>),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "createdAt",
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          >
            {t("teacher.createdat")}
            <ArrowUpDown />
          </Button>
        )
      },
      cell: ({ row }) => (
        <div>{row.original.createdAt ? format(row.original.createdAt, "MM/dd/yyyy") : ""}</div>
      ),
    },
    {
      accessorKey: "dueDate",
      header: () => { return (<span>{t("common.due")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.dueDate ? format(row.original.dueDate, "MM/dd/yyyy") : ""}</div>
      ),
    },
    {
      accessorKey: "title",
      header: () => { return (<span>{t("common.title")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.title}</div>
      ),
    },
    {
      accessorKey: "description",
      header: () => { return (<span>{t("common.description")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.description ?? ""}</div>
      ),
    },
    {
      accessorKey: "topicList",
      header: () => { return (<span>{t("common.unit")}</span>) },
      cell: ({ row }) => (
        <div className='w-32 truncate'>{row.original.topicList.join(',')}</div>
      ),
    },
    {
      accessorKey: "className",
      header: () => { return (<span>{t("common.class")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.classInfo.name}</div>
      ),
    },
    {
      accessorKey: "submittedCount",
      header: () => { return (<span>{t("common.submitcount")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.submittedCount}</div>
      ),
    },
    {
      accessorKey: "correctCount",
      header: () => { return (<span>{t("common.correctcount")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.correctCount}</div>
      ),
    },
    {
      accessorKey: "totalCount",
      header: () => { return (<span>{t("common.totalcount")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.totalCount}</div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        const info = row.original;

        return (
          <div className='flex place-content-evenly gap-2'>
            <NavLink to={ROUTE.TEACHER.HOMEWORK_CLASSVIEW} state={{ assignmentId: info.assignmentId, classInfo: info.classInfo }}><Button variant="outline">{t("teacher.inspect")}</Button></NavLink>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive">{t("teacher.deleteassignment")}</Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t('teacher.confirmdeleteassignment')}</AlertDialogTitle>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("component.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={async () => {
                    try {
                      const rsp = await deleteAssignment({ assignmentId: info.assignmentId });
                      if (rsp.code === 200) {
                        // 刷新表格数据
                        await refreshAssignmentData();
                      } else {
                        console.error('Delete assignment failed:', rsp.message);
                        alert(`删除课业失败: ${rsp.message}`);
                      }
                    } catch (error) {
                      console.error('deleteAssignment error', error);
                      alert('删除课业时发生错误，请稍后重试');
                    }
                  }}>{t("teacher.deleteassignment")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={() => handlePreview.current(info)}>{t("teacher.print")}</Button>
          </div>
        )
      },
    },
  ]);

  const [classColumns] = React.useState<ColumnDef<ClassTableData>[]>([
    {
      id: "select",
      header: () => (<></>),
      cell: () => (<></>),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "title",
      header: () => { return (<span>{t("common.title")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.title}</div>
      ),
    },
    {
      accessorKey: "name",
      header: () => { return (<span>{t("common.class")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.name}</div>
      ),
    },
    {
      accessorKey: "studentCount",
      header: () => { return (<span>{t("component.studentcount")}</span>) },
      cell: ({ row }) => (
        <div>{row.original.studentCount}</div>
      ),
    },
    {
      id: "actions",
      enableHiding: false,
      cell: ({ row }) => {
        return (
          <div className='flex place-content-evenly'>
            <NavLink to={ROUTE.TEACHER.PRACTICE_CLASSVIEW} state={row.original}><Button variant="outline">{t("teacher.inspect")}</Button></NavLink>
          </div>
        )
      },
    },
  ]);

  const updateStudentMessage = () => {
    getStudentMessage().then((rsp) => {
      if (rsp.code === 200 && rsp.data) {
        const validStudentMessages = rsp.data.studentMessages.filter((studentMessage) => !studentMessage.teacherMessage);

        // 分离学生求助 (type = 0) 和批改作业 (type = 1)
        const helpMessages = validStudentMessages.filter((msg) => msg.type === 0);
        const reviewMessages = validStudentMessages.filter((msg) => msg.type === 1);

        helpMessages.sort((a, b) => a.createdAt! > b.createdAt! ? 1 : 0);
        reviewMessages.sort((a, b) => a.createdAt! > b.createdAt! ? 1 : 0);

        setStudentMessages(helpMessages);
        setHomeworkReviews(reviewMessages);
      }
    }).catch((e) => {
      console.error("Error fetching assignment list", e);
    });
  }

  // 筛选数据的函数
  const filterTableData = React.useCallback((data: TableData[], classId: string) => {
    if (classId === "all") {
      return data;
    }
    return data.filter(item => item.classInfo.id?.toString() === classId);
  }, []);

  // 刷新作业数据的函数
  const refreshAssignmentData = React.useCallback(async () => {
    try {
      const rsp = await getAssignmentListByTeacher({ teacherId: getCurrentUser()!.id });
      if (rsp.code === 200 && rsp.data) {
        const allData = rsp.data.assignmentList.map((model: AssignmentModel, index) => {
          const stats = rsp.data!.assignmentStatsList[index] as AssignmentStats;
          return {
            title: model.assignment.title,
            description: model.assignment.description,
            dueDate: model.assignment.dueDate,
            createdAt: model.assignment.createdAt,
            subject: getSubjectDisplayName(subjectInfos, model.subjectId, model.subject, i18n.language),
            topicList: model.topicList,
            classInfo: model.classInfo,
            totalCount: stats.totalCount,
            submittedCount: stats.submittedCount,
            correctCount: stats.correctCount,
            errorKnowledgePoints: stats.errorKnowledgePoints,
            assignmentId: model.assignment.id!,
          }
        });

        setAllTableData(allData);
        setTableData(filterTableData(allData, selectedClassId));
      }
    } catch (e) {
      console.error("Error fetching assignment list", e);
    }
  }, [filterTableData, selectedClassId, getCurrentUser]);

  React.useEffect(() => {
    refreshAssignmentData();
    updateStudentMessage();
  }, [refreshAssignmentData]);

  // 当班级筛选改变时，重新筛选数据
  React.useEffect(() => {
    if (allTableData) {
      setTableData(filterTableData(allTableData, selectedClassId));
    }
  }, [selectedClassId, allTableData, filterTableData]);

  React.useEffect(() => {
    const getClassInfo = async () => {
      const rsp = await getAllClassInfo({schoolName: getCurrentUser()!.schoolName});
      if (rsp.code === 200 && rsp.data) {
        const classData: ClassTableData[] = [];
        for (const classInfo of rsp.data.classroomInfoResponseList) {
          classData.push({
            title: t('student.practice'),
            name: classInfo.name,
            id: classInfo.id,
            studentCount: classInfo.studentCount,
          })
        }
        setClassData(classData);
      }
    }
    getClassInfo();
  }, [getCurrentUser, t]);

  return (
    <ContentLayout title={t("teacher.homework")}>
      {showSurvey6 && (
        <Survey6 onSuccess={() => setShowSurvey6(false)} />
      )}
      <div className="mx-auto space-y-6 p-2">
        <div className="bg-white shadow-md rounded-lg">
          <DataTable
            data={tableData ?? []}
            columns={columns}
            filterColumn='title'
            filterName={t('common.title')}
            renderButton={() => (
              <ClassFilterSelect
                classes={classData}
                value={selectedClassId}
                onChange={setSelectedClassId}
                placeholder={t("common.class")}
                allLabel={`${t("component.filter")} - ${t("common.class")}`}
              />
            )}
          />
        </div>

        {
          classData && classData.length > 0 &&
          <div className="bg-white shadow-md rounded-lg">
            <DataTable data={classData} columns={classColumns} filterColumn='title' filterName={t('common.title')} />
          </div>
        }

        {/* 学生求助和批改作业并排显示 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* 学生求助 */}
          <div className="border rounded-lg p-4 bg-muted shadow-md">
            <div className="flex flex-row items-center mb-4">
              <div className='w-1 h-5 bg-amber-500 rounded-2xl'>
              </div>
              <h2 className="text-lg font-semibold text-gray-800 pl-2">{t('teacher.studentmsg')}</h2>
            </div>
            <ScrollArea className="h-72">
              {
                studentMessages.map((studentMessage, index) => (
                  <div key={index} className="flex justify-between items-center mb-4 p-2 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      <User color='blue' className="w-10 h-10 rounded-full"></User>
                      <div className='flex flex-col'>
                        <p className="font-semibold text-sm">{format(new Date(studentMessage.createdAt!), t('teacher.dateformater', 'MM/dd'))}</p>
                        <p className="font-semibold text-sm">{studentMessage.studentName}{t('teacher.leavemsg')}</p>
                        <p className="text-sm text-gray-600">{studentMessage.description}</p>
                      </div>
                    </div>
                      <StudentMessageDialog
                        studentMessage={studentMessage}
                        className='gap-4 pr-4'
                        onUpdate={updateStudentMessage}
                      ></StudentMessageDialog>
                  </div>
                ))
              }
            </ScrollArea>
          </div>

          {/* 批改作业 */}
          <div className="border rounded-lg p-4 bg-muted shadow-md">
            <div className="flex flex-row items-center mb-4">
              <div className='w-1 h-5 bg-green-500 rounded-2xl'>
              </div>
              <h2 className="text-lg font-semibold text-gray-800 pl-2">{t('teacher.homeworkreview')}</h2>
            </div>
            <ScrollArea className="h-72">
              {
                homeworkReviews.map((homeworkReview, index) => (
                  <div key={index} className="flex justify-between items-center mb-4 p-2 border rounded-lg">
                    <div className="flex items-center space-x-4">
                      {/* Replace these with actual avatar images */}
                      <User color='green' className="w-10 h-10 rounded-full"></User>
                      <div className='flex flex-col'>
                        <p className="font-semibold text-sm">{format(new Date(homeworkReview.createdAt!), t('teacher.dateformater', 'MM/dd'))}</p>
                        <p className="font-semibold text-sm">{homeworkReview.studentName}{t('teacher.submitwork')}</p>
                        <p className="text-sm text-gray-600">{homeworkReview.description}</p>
                      </div>
                    </div>
                      <StudentMessageDialog
                        studentMessage={homeworkReview}
                        className='gap-4 pr-4'
                        onUpdate={updateStudentMessage}
                        // 批改作业导航功能
                        isReviewMode={true}
                        reviewList={homeworkReviews}
                        currentIndex={index}
                      ></StudentMessageDialog>
                  </div>
                ))
              }
            </ScrollArea>
          </div>

        </div>
      </div>

      <HomeworkPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        questions={previewQuestions}
        mode={PreviewMode.HOMEWORK}
        title={previewData?.title || ''}
        description={previewData?.description}
        subject={previewData?.subject}
        unit={previewData?.unit}
        className={previewData?.className}
        dueDate={previewData?.dueDate ? new Date(previewData.dueDate) : undefined}
      />
    </ContentLayout>
  );
};

export default HomeworkPage;
